import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useInventoryStore } from '../../../store/inventoryStore';
import { useWarehouseStore } from '../../../store/warehouseStore';
import { useProjectStore } from '../../../store/projectStore';
import { useSitesStore } from '../../../store/sitesStore';
import { useStockMovementStore, generatePendingId } from '../../../store/stockMovementStore';
import { Job, Site } from '../../../types/site';
import { colours, StepNav, Input, Select, FieldLabel } from './shared';
import { useOwnersStore } from '../../../store/ownersStore';

const formatJobReference = (jobTitle: string, siteName: string) => `${jobTitle} · ${siteName}`;

const StepIndicator: React.FC<{ steps: string[]; current: number }> = ({ steps, current }) => (
  <div style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '8px 16px 0', boxSizing: 'border-box', flexShrink: 0 }}>
    {steps.map((label, i) => {
      const done = i < current; const active = i === current;
      return (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22, borderRadius:'50%', fontSize:12, fontWeight:700, backgroundColor: done ? '#16a34a' : active ? '#3b82f6' : '#374151', color:'#fff', flexShrink:0 }}>{done ? '✓' : i + 1}</span>
            <span style={{ fontSize:13, fontWeight:500, whiteSpace:'nowrap', color: active ? colours.text : done ? '#6b7280' : '#4b5563' }}>{label}</span>
          </div>
          {i < steps.length - 1 && <div style={{ flex:1, height:1, margin:'0 16px', backgroundColor: done ? '#16a34a' : colours.border }} />}
        </React.Fragment>
      );
    })}
  </div>
);

interface Props { onQueued: () => void; onOpenReview?: () => void; }

const STEPS = ['Select Units', 'Check-Out Details'];

const CheckOutForm: React.FC<Props> = ({ onQueued, onOpenReview }) => {
  const { items, stockUnits, fetchItems, fetchStockUnits } = useInventoryStore();
  const { warehouses, storageAreasByWarehouse } = useWarehouseStore();
  const { currentProject } = useProjectStore();
  const { sites } = useSitesStore();
  const { addPending } = useStockMovementStore();
  const { owners: ownerList, fetchOwners } = useOwnersStore();

  const [step, setStep]               = useState(0);
  const [search, setSearch]           = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('');
  const [filterArea, setFilterArea]   = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Step 2
  const [owner, setOwner]         = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes]         = useState('');
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<{ job: Job; site: Site } | null>(null);

  useEffect(() => { fetchItems(); fetchStockUnits(); fetchOwners(); }, [fetchItems, fetchStockUnits, fetchOwners]);

  const itemById = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);

  const filterAreas = useMemo(() =>
    filterWarehouse ? (storageAreasByWarehouse[filterWarehouse] ?? []) : [],
    [storageAreasByWarehouse, filterWarehouse]
  );

  const inStockUnits = useMemo(() =>
    stockUnits.filter(u => u.status === 'in stock'),
    [stockUnits]
  );

  const pendingJobs = useMemo(() => {
    const jobs: Array<{ job: Job; site: Site }> = [];
    sites.forEach(site => {
      (site.jobs ?? [])
        .filter((job: Job) => job.status === 'pending')
        .forEach((job: Job) => jobs.push({ job, site }));
    });
    return jobs.sort((a, b) => a.job.title.localeCompare(b.job.title));
  }, [sites]);

  const clearJobSelection = () => {
    setSelectedJob(null);
    setReference('');
    setJobPickerOpen(false);
  };

  const selectJob = (entry: { job: Job; site: Site }) => {
    setSelectedJob(entry);
    setReference(formatJobReference(entry.job.title, entry.site.name));
    setJobPickerOpen(false);
  };

  const filtered = useMemo(() => {
    return inStockUnits.filter(u => {
      if (filterWarehouse && u.warehouseId !== filterWarehouse) return false;
      if (filterArea && u.storageAreaId !== filterArea) return false;
      if (search) {
        const q = search.toLowerCase();
        const item = itemById.get(u.itemId);
        return [item?.name, item?.sku, u.serialMac, String(u.unitId), u.owner]
          .filter(Boolean).join(' ').toLowerCase().includes(q);
      }
      return true;
    });
  }, [inStockUnits, filterWarehouse, filterArea, search, itemById]);

  const toggle = (id: string) =>
    setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () =>
    setSelectedIds(selectedIds.size === filtered.length ? new Set() : new Set(filtered.map(u => u.id)));

  const handleQueue = () => {
    addPending({
      type: 'check_out',
      id: generatePendingId(),
      unitIds: Array.from(selectedIds),
      owner,
      reference,
      notes,
    });
    setSelectedIds(new Set());
    setSearch('');
    setOwner('');
    setReference('');
    setNotes('');
    setJobPickerOpen(false);
    setSelectedJob(null);
    setStep(0);
    onQueued();
  };

  const { border, cardBg, headerBg, muted, text, inputBg, rowHover } = colours;

  return (
    <div className="flex-1 flex flex-col min-h-0" style={{ backgroundColor: colours.pageBg }}>
      <StepIndicator steps={STEPS} current={step} />

      {/* ── Step 1: Select in-stock units ── */}
      {step === 0 && (
        <div className="flex-1 flex flex-col min-h-0 px-4 py-4 gap-3">
          <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
            <div className="relative" style={{ minWidth: 260 }}>
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: muted }} />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, serial, unit ID…"
                className="w-full text-sm pl-8 pr-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ backgroundColor: inputBg, borderColor: border, color: text }}
              />
            </div>
            <select
              value={filterWarehouse}
              onChange={e => { setFilterWarehouse(e.target.value); setFilterArea(''); }}
              className="text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{ backgroundColor: inputBg, borderColor: border, color: text, minWidth: 160 }}
            >
              <option value="">All Warehouses</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            {filterAreas.length > 0 && (
              <select
                value={filterArea}
                onChange={e => setFilterArea(e.target.value)}
                className="text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ backgroundColor: inputBg, borderColor: border, color: text, minWidth: 140 }}
              >
                <option value="">All Areas</option>
                {filterAreas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            )}
          </div>

          <div className="flex-1 overflow-auto rounded" style={{ border: `1px solid ${border}` }}>
            <table className="w-full">
              <thead className="sticky top-0" style={{ backgroundColor: headerBg }}>
                <tr style={{ borderBottom: `1px solid ${border}` }}>
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleAll} />
                  </th>
                  {['Item', 'Unit ID', 'Serial/MAC', 'Warehouse', 'Storage Area', 'Condition', 'Current Owner'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs uppercase tracking-wide font-medium" style={{ color: muted }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ backgroundColor: cardBg }}>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-sm" style={{ color: muted }}>
                    {inStockUnits.length === 0 ? 'No in-stock units available.' : 'No units match filters.'}
                  </td></tr>
                )}
                {filtered.map((unit, idx) => {
                  const item  = itemById.get(unit.itemId);
                  const wh    = warehouses.find(w => w.id === unit.warehouseId);
                  const areas = storageAreasByWarehouse[unit.warehouseId] ?? [];
                  const area  = areas.find(a => a.id === unit.storageAreaId);
                  const sel   = selectedIds.has(unit.id);
                  return (
                    <tr
                      key={unit.id}
                      className="cursor-pointer transition-colors"
                      style={{
                        borderBottom: idx < filtered.length - 1 ? `1px solid ${border}` : undefined,
                        backgroundColor: sel ? '#1d2535' : undefined,
                      }}
                      onClick={() => toggle(unit.id)}
                      onMouseEnter={e => { if (!sel) (e.currentTarget as HTMLElement).style.backgroundColor = rowHover; }}
                      onMouseLeave={e => { if (!sel) (e.currentTarget as HTMLElement).style.backgroundColor = ''; }}
                    >
                      <td className="px-3 py-1.5"><input type="checkbox" checked={sel} onChange={() => toggle(unit.id)} onClick={e => e.stopPropagation()} /></td>
                      <td className="px-3 py-1.5 text-sm font-medium" style={{ color: text }}>{item?.name ?? '—'}</td>
                      <td className="px-3 py-1.5">
                        <span className="inline-flex items-center justify-center text-xs font-medium rounded px-1.5 py-0.5" style={{ backgroundColor: '#374151', color: '#d1d5db' }}>
                          {unit.unitId}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-sm" style={{ color: unit.serialMac ? text : muted }}>{unit.serialMac || '—'}</td>
                      <td className="px-3 py-1.5 text-sm" style={{ color: text }}>{wh?.name ?? '—'}</td>
                      <td className="px-3 py-1.5 text-sm" style={{ color: area ? text : muted }}>{area?.name ?? '—'}</td>
                      <td className="px-3 py-1.5 text-sm capitalize" style={{ color: muted }}>{unit.condition ?? '—'}</td>
                      <td className="px-3 py-1.5 text-sm" style={{ color: unit.owner ? text : muted }}>{unit.owner || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {selectedIds.size > 0 && (
            <p className="text-sm flex-shrink-0" style={{ color: muted }}>{selectedIds.size} unit{selectedIds.size > 1 ? 's' : ''} selected</p>
          )}
        </div>
      )}

      {/* ── Step 2: Check-out details ── */}
      {step === 1 && (
        <div className="flex-1 overflow-auto px-4 py-4">
          <div
            className="max-w-md rounded p-5 flex flex-col gap-4"
            style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}
          >
            <p className="text-sm" style={{ color: muted }}>
              Checking out {selectedIds.size} unit{selectedIds.size > 1 ? 's' : ''}. Who are they going to?
            </p>
            <Select
              label="Owner / Person *"
              value={owner}
              onChange={e => setOwner(e.target.value)}
              wFull
            >
              <option value="">Select owner…</option>
              {ownerList.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
            </Select>
            {ownerList.length === 0 && (
              <p className="text-sm" style={{ color: muted }}>
                No owners yet — add them under Preferences → Owners.
              </p>
            )}

            {/* Reference — manual entry or linked to a pending job */}
            <div className="flex flex-col gap-1 w-full">
              <div className="flex items-center justify-between gap-2">
                <FieldLabel>Reference (optional)</FieldLabel>
                {!selectedJob && !jobPickerOpen && (
                  <button
                    type="button"
                    onClick={() => setJobPickerOpen(true)}
                    className="text-sm transition-colors hover:underline"
                    style={{ color: '#60a5fa' }}
                  >
                    is this going for a job?
                  </button>
                )}
                {selectedJob && (
                  <button
                    type="button"
                    onClick={clearJobSelection}
                    className="text-sm transition-colors hover:underline"
                    style={{ color: muted }}
                  >
                    Cancel
                  </button>
                )}
              </div>

              <input
                type="text"
                placeholder="Job number, project…"
                value={reference}
                onChange={e => setReference(e.target.value)}
                disabled={!!selectedJob}
                className="text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 w-full disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ backgroundColor: inputBg, borderColor: border, color: text }}
              />

              {jobPickerOpen && (
                <div
                  className="rounded overflow-hidden mt-1"
                  style={{ border: `1px solid ${border}`, backgroundColor: colours.pageBg }}
                >
                  <div
                    className="flex items-center justify-between px-3 py-2"
                    style={{ borderBottom: `1px solid ${border}`, backgroundColor: headerBg }}
                  >
                    <span className="text-xs uppercase tracking-wide font-medium" style={{ color: muted }}>
                      Pending Jobs
                    </span>
                    <button
                      type="button"
                      onClick={() => setJobPickerOpen(false)}
                      className="text-sm transition-colors hover:underline"
                      style={{ color: muted }}
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="max-h-48 overflow-auto">
                    {pendingJobs.length === 0 ? (
                      <p className="px-3 py-4 text-sm text-center" style={{ color: muted }}>
                        No pending jobs found.
                      </p>
                    ) : (
                      pendingJobs.map(({ job, site }) => (
                        <button
                          key={`${site.id}-${job.id}`}
                          type="button"
                          onClick={() => selectJob({ job, site })}
                          className="w-full text-left px-3 py-2.5 transition-colors"
                          style={{ borderBottom: `1px solid ${border}` }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = rowHover)}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                        >
                          <div className="text-sm font-medium" style={{ color: text }}>{job.title}</div>
                          <div className="text-sm mt-0.5" style={{ color: muted }}>{site.name}</div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <Input label="Notes (optional)" placeholder="Any notes…" value={notes} onChange={e => setNotes(e.target.value)} wFull />
          </div>
        </div>
      )}

      <StepNav
        onBack={step > 0 ? () => setStep(0) : undefined}
        onNext={step === 0 ? () => setStep(1) : undefined}
        onQueue={step === 1 ? handleQueue : undefined}
        onReview={onOpenReview}
        nextDisabled={selectedIds.size === 0}
        queueDisabled={!owner.trim()}
        queueLabel="Add to Queue ✓"
      />
    </div>
  );
};

export default CheckOutForm;
